import type { CamelCaseSmartrrFlags } from "@smartrr/shared/LaunchDarkly";
import { camelize } from "@smartrr/shared/utils/camelize";
import { getFromLocalStorage } from "@smartrr/shared/utils/localStorageAccess";
import { ldGetClientSideId } from "./ldGetClientSideId";

/**
 * To be used in areas where hooks (`useSmartrrFlags()`) are unavailable
 */
export const getLdFlagsFromLocalStorage = () => {
  const clientSideId = ldGetClientSideId();
  return getLdFlagsFromLocalStorageWithClientId(clientSideId);
};

const LD_CLIENT_SIDE_ID_STORAGE_KEY = "ld:clientId";

function getLdFlagsFromLocalStorageWithClientId(clientSideId?: string) {
  // If clientSideId is not passed as param we'll instead fetch the id stored in localStorage during app init.
  // This likely will only be utilized within /shared where ldGetClientSideId is not accessible
  clientSideId = clientSideId || getFromLocalStorage(LD_CLIENT_SIDE_ID_STORAGE_KEY);
  // If no clientSideId then we have no way of ensuring which localStorage entry is most accurate so returning null
  if (!clientSideId) {
    return null;
  }

  const key = Array.from({ length: localStorage.length })
    .map((_, i) => localStorage.key(i))
    //  local storage key for LaunchDarkly is formatted as follows: id:CLIENT_ID:LD_ID
    .find(key => key?.includes(clientSideId) && !key?.includes("$diagnostics"));
  const localStorageValue = key ? JSON.parse(localStorage.getItem(key)!) : null;

  // `useCamelCaseFlagKeys` as part of LD's `useFlags` automatically converts their kebab case keys to camel. Here we're doing that manually
  return localStorageValue
    ? (Object.fromEntries(
        Object.entries(localStorageValue).map(([key, val]) => [camelize(key), val])
      ) as LaunchDarklyLocalStorageFlags)
    : null;
}

interface LaunchDarklyLocalStorageValue {
  flagVersion: number;
  trackEvents: boolean;
  value: boolean | string;
  variation: number;
  version: number;
}

type LaunchDarklyLocalStorageFlags = Record<keyof CamelCaseSmartrrFlags, LaunchDarklyLocalStorageValue>;
